import { useMemo, useCallback, useState } from "react";
import { Button } from "@aidkitorg/component-library";
import { IssueDateFilter } from "./IssueDateFilter";
import { DueDateFilter } from "./DueDateFilter";
import { StatusFilter } from "./StatusFilter";
import { useLocalizedStrings } from "../../../../Localization";
import { InvoiceFilterParams, Invoice } from "../../../../Contexts/InvoicesContext";

export type { InvoiceFilterParams };

interface InvoiceFiltersProps {
  onFiltersChange: (filters: InvoiceFilterParams) => void;
}

export const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
  onFiltersChange,
}) => {
  const L = useLocalizedStrings();
  
  // Local state for managing filters before applying them
  const [localFilters, setLocalFilters] = useState<InvoiceFilterParams>({
    issueStartDate: "",
    issueEndDate: "",
    dueStartDate: "",
    dueEndDate: "",
    statuses: [],
  });

  // Available status options
  const availableStatuses = useMemo((): Invoice['status'][] => [
    "Unpaid",
    "Paid", 
    "Partially Paid",
    "Partially Credited",
    "Credited",
    "Overdue",
    "Pending"
  ], []);

  // Filter handlers for Issue Date
  const handleIssueStartDateChange = useCallback((date: string) => {
    setLocalFilters(prev => ({ ...prev, issueStartDate: date }));
  }, []);

  const handleIssueEndDateChange = useCallback((date: string) => {
    setLocalFilters(prev => ({ ...prev, issueEndDate: date }));
  }, []);

  // Filter handlers for Due Date
  const handleDueStartDateChange = useCallback((date: string) => {
    setLocalFilters(prev => ({ ...prev, dueStartDate: date }));
  }, []);

  const handleDueEndDateChange = useCallback((date: string) => {
    setLocalFilters(prev => ({ ...prev, dueEndDate: date }));
  }, []);

  // Filter handlers for Status
  const handleStatusesChange = useCallback((statuses: string[]) => {
    setLocalFilters(prev => ({ ...prev, statuses: statuses as Invoice['status'][] }));
  }, []);

  // Apply filters function
  const applyFilters = useCallback(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(localFilters).filter(([_, value]) => 
        value !== "" && (Array.isArray(value) ? value.length > 0 : true)
      )
    );
    onFiltersChange(cleanFilters);
  }, [localFilters, onFiltersChange]);

  // Reset filters function
  const resetFilters = useCallback(() => {
    const emptyFilters = {
      issueStartDate: "",
      issueEndDate: "",
      dueStartDate: "",
      dueEndDate: "",
      statuses: [],
    };
    setLocalFilters(emptyFilters);
    onFiltersChange({});
  }, [onFiltersChange]);

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        <IssueDateFilter
          startDate={localFilters.issueStartDate || ""}
          endDate={localFilters.issueEndDate || ""}
          onStartDateChange={handleIssueStartDateChange}
          onEndDateChange={handleIssueEndDateChange}
        />
        <DueDateFilter
          startDate={localFilters.dueStartDate || ""}
          endDate={localFilters.dueEndDate || ""}
          onStartDateChange={handleDueStartDateChange}
          onEndDateChange={handleDueEndDateChange}
        />
        <StatusFilter
          selectedStatuses={localFilters.statuses || []}
          availableStatuses={availableStatuses}
          onStatusesChange={handleStatusesChange}
        />
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>
            {L.admin_panel.invoices.filters.reset_filters}
          </Button>
          <Button onClick={applyFilters}>
            {L.admin_panel.invoices.filters.apply_filters}
          </Button>
        </div>
      </div>
    </div>
  );
};
